// frontend/src/AppTypes.ts
// Gemeinsame Typdefinitionen für das Frontend. Sämtliche Module beziehen sich auf
// diese Strukturen, damit Datenfluss und Serialisierung zwischen UI und Backend klar bleiben.
export type Settings = {
  // Projektweite Metadaten, spiegeln direkt die XML-Settings wider.
  // Diese Einstellungen definieren den zeitlichen Rahmen und regionale Besonderheiten.
  version: string;
  hue: number;
  language: string;
  region: string;
  state: string;
  calendar: string;
  timezone: string;
};

export type Visual = {
  // Optionales Styling-Delta, das im UI zur farblichen Darstellung von Tags/Points genutzt wird.
  type: "default" | "lightness" | "chroma" | "hue";
  value?: number;
};

// Dimref-Struktur, die mit dem Solver-IR (DimRefSpec) kompatibel ist.
// Diese Struktur wird beim Export in XML verwendet.
export type DimRef = {
  dimension: string;
  role?: "for" | "where" | "on" | "each" | string; // erlaubt bekannte Rollen, aber auch andere
  selector_type: "allpoints" | "pointref" | "tagref";
  point_names?: string[];
  tag_names?: string[];
};

// Eine Dimension kann Tags enthalten, die als Attribute für Punkte dienen.
export type Tag = {
  id: string; // Eindeutige ID des Tags innerhalb der Dimension
  name: string; // Anzeigename
  visual: Visual; // Visuelle Gestaltung (Farbe/Helligkeit)
};

// Ein Punkt ist ein konkretes Element einer Dimension (z.B. ein spezifischer Mitarbeiter oder Ort).
export type Point = {
  id: string; // Eindeutige ID
  name: string; // Anzeigename
  tagRefs: string[]; // Referenzen auf Tags, die diesem Punkt zugeordnet sind
  visual: Visual; // Visuelle Gestaltung
  startDate?: string; // Optional: Startdatum für time.period-Points (ISO, z.B. "2026-02-02")
  dayOfPeriod?: number; // Optional: Tagesindex für time.day-Points (z.B. 1..7)
  startTime?: string;       // Optional: Startzeit für time.slot-Points (HH:MM, z.B. "08:00")
  durationMinutes?: number; // Optional: Dauer in Minuten für time.slot-Points (z.B. 45)
  template?: boolean; // Optional: Nur für time.period-Points – markiert als Template-Woche
};

// Erlaubte Typen für eine Dimension.
export type DimensionType =
  | "none"
  | "time.day"
  | "time.slot"
  | "time.period"
  | "resource.team"
  | "resource.person"
  | "resource.place"
  | "resource.asset"
  | "topic";

// Zentrale Struktur für Dimensionen (z.B. Zeit, Mitarbeiter, Orte).
export type Dimension = {
  // Eine Dimension gruppiert Tags und Points.
  id: string;
  name: string;
  dimType?: DimensionType; // Semantischer Typ der Dimension (Default: "none")
  tags: Tag[]; // Liste der definierten Tags
  points: Point[]; // Liste der definierten Punkte
  startDate?: string; // Nur für time.period: Startdatum (ISO, z.B. "2026-02-02")
  endDate?: string;   // Nur für time.period: Enddatum (ISO, z.B. "2026-07-31")
  numberOfDays?: number; // Nur für time.period: Länge einer Periode in Tagen (Default: 7)
  lookahead?: number; // Nur für time.period: Anzahl Wochen voraus sichtbar
  publish?: number;   // Nur für time.period: Anzahl Wochen voraus veröffentlicht
  calendarMaxLanes?: number; // Nur für time.slot: maximale Anzahl nebeneinander sichtbarer Karten (Default: 1)
};

export type RelationTarget = {
  // Beschreibt, auf welche Bestandteile einer Dimension sich eine Relation bezieht.
  dimensionId: string;
  mode: "all" | "tags" | "points";
  tagIds: string[];
  pointIds: string[];
};

// Definiert Beziehungen zwischen Dimensionen.
export type Relation = {
  // Business-Regel, die Verbindungen (connection) oder Ausschlüsse (disconnection) modelliert.
  // Beispielsweise: "Mitarbeiter A kann nicht an Ort B arbeiten".
  id: string;
  name: string;
  type: "connection" | "disconnection"; // Art der Beziehung
  targets: RelationTarget[]; // Die betroffenen Dimensionen/Elemente
  scope?: "always" | "template" | "period" | "day" | "repair";
  mode?: "any" | "shortterm" | "longterm"; // any = beliebig (Default), shortterm = kurzfristig, longterm = langfristig
};

// Fixierungen sperren bestimmte Bereiche des Plans (z.B. Vergangenheit oder veröffentlichte Wochen).
export type Fixation = {
  id: string;
  name: string;
  targets: RelationTarget[];
};

// Manuelle Eingriffe in den Plan (z.B. Abwesenheiten oder fixierte Einträge).
export type Adjustment = {
  id: string;
  name: string;
  type: "off" | "pin"; // off = Abwesenheit (start_date + end_date), pin = fixer Eintrag (date)
  startDate?: string;  // nur für type "off" (ISO-Datum, z.B. "2026-02-15")
  endDate?: string;    // nur für type "off"
  date?: string;       // nur für type "pin"
  targets: RelationTarget[];
};

// Zeitfenster-Definitionen, die im Planer als Referenz genutzt werden können.
export type TimingAnchor =
  | "today_start"
  | "tomorrow_start"
  | "yesterday_start"
  | "current_week_start"
  | "current_month_start"
  | "planning_horizon_start"
  | "planning_horizon_end";

export type Timing = {
  id: string;
  name: string;
  type: "relative" | "absolute";
  // Nur für type="relative":
  anchor?: TimingAnchor;
  direction?: "future" | "past";
  length?: string;       // Anzahl Einheiten (z.B. "7")
  unit?: "day";          // vorerst nur "day"
  offset?: number;       // optional, z.B. 3
  // Nur für type="absolute" (mindestens eines davon):
  start_date?: string;   // ISO, z.B. "2026-07-01"
  end_date?: string;     // ISO, z.B. "2026-08-01"
};

// Kapazitäten definieren Limits (z.B. maximale Anzahl von Mitarbeitern in einem Bereich).
export type Capacity = {
  id: string;
  name: string;
  type: "limit";
  limitValue: number;
  for: RelationTarget[];
  where: RelationTarget[];
  scope?: "always" | "template" | "period" | "day" | "repair";
  // always = Default, in normalen Solves aktiv
  // template = nur im Template-/Vorlagen-Solve
  // period = nur im operativen Perioden-Solve
  // day = nur im kurzfristigen Tages-Repair
  // repair = nur bei Adjustment-/Notfall-Reparaturen
  mode?: "any" | "shortterm" | "longterm"; // any = beliebig (Default), shortterm = kurzfristig, longterm = langfristig
};

export type Creation = {
  id: string;
  name: string;
  type: "equal" | "maximize";
  equalValue: number;
  enforcement: "hard" | "off";
  connect: boolean;
  make: RelationTarget[];
  for: RelationTarget[];
};

export type Metric = {
  id: string;
  name: string;
  type: "count" | "time.sum";
  time_ref?: string;
  dimrefs: {
    id: string;
    dimensionId: string;
    role: "for" | "where";
    mode: "all" | "tags" | "points";
    tagIds: string[];
    pointIds: string[];
  }[];
};

// Objective – nach neuem Schema.
export type Objective = {
  id: string; // Internal frontend ID
  name: string;
  type: "minimize" | "maximize" | "target" | "balance";
  metric_ref: string;
  target?: string;
};

// Gruppierung von Bedingungen für bessere Übersicht im UI.
export type ConditionGroup = {
  id: string;
  name: string;
  enabled: boolean; // Steuert, ob die Bedingungen in dieser Gruppe vom Solver berücksichtigt werden (true) oder ignoriert (false)
};

// Align Participant: Gruppierung von DimRefs für align conditions
export type AlignParticipant = {
  dimrefs: DimRef[];
};

export type Condition = {
  // Solver-Bedingung inkl. optionaler Parameter. groupId ordnet sie einer UI-Gruppe zu.
  // HINWEIS: dimrefs, participants, on sind nur in ExtendedCondition (UI-Ebene).
  // Der DimRef-Typ wird für XML-Export/Import verwendet, nicht für den Runtime-DataModel.
  id: string;
  name: string;
  type: "count" | "distinct" | "gap" | "block" | "align" | "follow" | "time.sum" | "time.gap";
  enforcement?: "hard" | "soft" | "off"; // hard=muss erfüllt sein, soft=Default (Reihenfolge zählt), off=nicht behandelt
  patternType?: string;
  patternValue?: string;
  time_ref?: string; // Optional: Referenz auf ein <timing name="...">
  groupId?: string; // Referenz zur Gruppe
  scope?: "always" | "template" | "period" | "day" | "repair";
  mode?: "any" | "shortterm" | "longterm"; // any = beliebig (Default), shortterm = kurzfristig, longterm = langfristig
};

/* Veraltete Struktur für Quality Records - kann evtl. entfernt werden */
export type QRecord = {
  ConditionName?: string;
  Fulfillment?: number;
};

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

// "Backend liefert Listen von Records" – wir erzwingen: Array von JSON-Objekten.
// Damit verhindert TS wiederkehrend "forEach ok, aber entry ist string/null".
export type JsonObject = { [key: string]: JsonValue };
export type FulfillmentsData = JsonObject[];
export type SyntaxchecksData = JsonObject[];

// Das Herzstück des Frontend-Zustands.
// Beinhaltet alle Konfigurationen, Regeln und den aktuellen Plan.
export type DataModel = {
  // Vollständiges Datenmodell, das der Plan-Editor lokal hält und an Backend/LocalStorage spiegelt.
  settings: Settings; // Globale Einstellungen
  dimensions: Dimension[]; // Liste aller Dimensionen
  timings: Timing[]; // Liste aller Timing-Definitionen (zwischen Dimensionen und Relationen)
  relations: Relation[]; // Liste aller Relationen
  creations: Creation[]; // Liste aller Creation-Regeln
  capacities: Capacity[]; // Liste aller Kapazitäten
  conditions: Condition[]; // Liste aller Bedingungen
  conditionGroups: ConditionGroup[]; // Gruppenstruktur für Bedingungen
  metrics: Metric[]; // Liste aller Metrics (Planwerte ohne Solver-Wirkung)
  objectives: Objective[]; // Liste aller Objectives (Creation↔Condition-Verknüpfungen)
  fixations: Fixation[]; // Liste aller Fixierungen
  adjustments: Adjustment[]; // Liste aller manuellen Eingriffe
  rules_xml: string; // Die rohe XML-Repräsentation der Regeln (für Backup/Export)
  plan_csv: string; // Der aktuelle Plan im CSV-Format
  fulfillments_json: FulfillmentsData | null; // Ergebnisse der Regelprüfung (vom Backend)
  syntaxchecks_json: SyntaxchecksData | null; // Ergebnisse der Syntaxprüfung (vom Backend)
};

/* ---------- Default-Daten ---------- */
// Dienen als Ausgangspunkt beim ersten Laden der App oder wenn das Backend noch
// keine Projektinformationen geliefert hat.
export const defaultData: DataModel = {
  settings: {
    version: "",
    hue: 220,
    language: "",
    region: "",
    state: "",
    calendar: "",
    timezone: "",
  },
  dimensions: [],
  timings: [],
  relations: [],
  creations: [],
  capacities: [],
  conditions: [],
  conditionGroups: [
    { id: "group-default", name: "Group1", enabled: true },
  ],
  metrics: [],
  objectives: [],
  fixations: [],
  adjustments: [],
  rules_xml: "",
  plan_csv: "",
  fulfillments_json: null,
  syntaxchecks_json: null,
};
